import React, { useState, useCallback, SyntheticEvent } from 'react';
import { Clickable, Stack, Text, RadioButton, Input, Button, Overlay, ControlLink } from '@moda/om';
import classNames from 'classnames';
import { reportError } from '../../lib/reportError';
import { useErrorModal } from '../../hooks/useErrorModal';
import { LoadingBalls } from '../LoadingBalls';
import { useGiftCode, getInvalidCodeMessage } from './useGiftCode';

import './GiftCode.scss';

enum Mode {
  Resting,
  Editing,
  Saving
}

interface Props {
  theme?: 'box' | 'simple';
}

export const GiftCode: React.FC<Props> = ({ theme = 'box' }) => {
  const [mode, setMode] = useState(Mode.Resting);
  const [inputGiftCode, setInputGiftCode] = useState('');
  const { showErrorModal } = useErrorModal();
  const { giftCode, invalidCodeMessage, applyGiftCode } = useGiftCode();

  const closeEditView = useCallback(() => {
    setMode(Mode.Resting);
    setInputGiftCode('');
  }, [setMode, setInputGiftCode]);

  const submitCode = useCallback(
    async (submittedCode: string | null) => {
      setMode(Mode.Saving);

      try {
        const code = submittedCode?.toUpperCase();
        const result = await applyGiftCode(code || null);
        getInvalidCodeMessage(result.data) ? setMode(Mode.Editing) : closeEditView();
      } catch (error) {
        showErrorModal();
        reportError(error);
      }
    },
    [setMode, applyGiftCode, showErrorModal, closeEditView]
  );

  const onEdit = useCallback(() => setMode(Mode.Editing), [setMode]);

  const onApply = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      inputGiftCode ? submitCode(inputGiftCode) : closeEditView();
    },
    [inputGiftCode, submitCode, closeEditView]
  );

  const onCancel = useCallback(() => (giftCode ? submitCode(null) : closeEditView()), [
    giftCode,
    submitCode,
    closeEditView
  ]);

  return (
    <>
      <Stack
        space={4}
        className={classNames('GiftCode', `GiftCode--${theme}`, { 'GiftCode--empty': !giftCode })}
      >
        <div className="GiftCode__head">
          <Stack space={4}>
            <Text treatment="eyebrow">Gift Code</Text>
            {mode === Mode.Resting && giftCode && (
              <Text treatment="eyebrow" color="cement">
                {giftCode}
              </Text>
            )}
          </Stack>
          {mode === Mode.Resting && (
            <ControlLink aria-label="Add Gift Code" onClick={onEdit} data-testid="changeGiftCode">
              {giftCode ? 'Change' : 'Add'}
            </ControlLink>
          )}
        </div>
        {mode !== Mode.Resting && (
          <form onSubmit={onApply}>
            <Stack space={4}>
              <Text treatment="body2" color="cement">
                Limit one gift code per order
              </Text>
              <Stack space={2}>
                {giftCode && !invalidCodeMessage && (
                  <Stack className="GiftCode__row" direction="horizontal" space={2}>
                    <RadioButton readOnly checked={!inputGiftCode} />
                    <Text treatment="eyebrow" color="cement">
                      {giftCode}
                    </Text>
                  </Stack>
                )}
                <Stack className="GiftCode__row" direction="horizontal" space={2}>
                  <RadioButton readOnly checked={inputGiftCode !== ''} />
                  <Input
                    className="GiftCode__input"
                    error={invalidCodeMessage || false}
                    placeholder="Enter Gift Code"
                    value={inputGiftCode}
                    onChange={event => setInputGiftCode(event.target.value)}
                    data-testid="giftCodeInput"
                  />
                </Stack>
              </Stack>
              <Button aria-label="Apply Gift Code" type="submit" data-testid="applyGiftCode">
                Apply
              </Button>
              <Clickable
                type="button"
                className="GiftCode__cancel"
                onClick={onCancel}
                data-testid="cancelGiftCode"
              >
                I do not want to apply a gift code
              </Clickable>
            </Stack>
          </form>
        )}
      </Stack>

      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />
        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
